<template>
    <span
        ref="trigger"
        class="popover-trigger"
        :class="{ 'is-disabled': disabled }"
        :tabindex="disabled ? -1 : 0"
        @click.stop="handleClick"
    >
        <slot name="trigger">
            <span ref="triggerNode" class="popover-default-trigger">
                <slot></slot>
            </span>
        </slot>
        <portal v-if="hasBeenOpened" to="overlay">
            <MorphingGhost
                :from-element="$refs.triggerNode || $refs.trigger"
                :to-element="$refs.popover"
                :is-open="visible"
                :speed="speed"
                @animation-complete="handleAnimationComplete"
            />
            <div
                v-show="visible"
                ref="popover"
                class="popover"
                :class="{ 'is-morphing': isMorphing }"
                :style="popoverStyle"
                @click.stop
            >
                <div class="popover-content" :class="{ 'is-content-hidden': !isContentVisible }">
                    <slot name="content" :close="close">
                        <div class="popover-default-content">
                            <slot name="default"></slot>
                        </div>
                    </slot>

                    <div v-if="$slots.actions" class="popover-actions">
                        <slot name="actions" :close="close" />
                    </div>
                </div>
            </div>
        </portal>
    </span>
</template>

<script>
import { Portal } from 'portal-vue';
import MorphingGhost from '@/components/animations/MorphingGhost.vue';

let openPopoverInstance = null;

export default {
    name: 'Popover',
    components: { Portal, MorphingGhost },
    props: {
        trigger: {
            type: String,
            default: 'click',
            validator: (v) => ['click'].includes(v),
        },
        closeOnClickOutside: {
            type: Boolean,
            default: true,
        },
        closeOnEsc: {
            type: Boolean,
            default: true,
        },
        width: {
            type: String,
            default: null,
        },
        minWidth: {
            type: String,
            default: '240px',
        },
        maxWidth: {
            type: String,
            default: '380px',
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        speed: {
            type: String,
            default: 'fast',
            validator: (v) => ['slowest', 'slow', 'normal', 'fast', 'fastest'].includes(v),
        },
        horizontalPosition: {
            type: String,
            default: 'auto',
            validator: (v) => ['auto', 'left', 'center', 'right'].includes(v),
        },
    },

    data() {
        return {
            visible: false,
            hasBeenOpened: false,
            isMorphing: false,
            isContentVisible: false,
            popoverStyle: {},
        };
    },
    computed: {
        contentDelay() {
            const delays = {
                slowest: 350,
                slow: 250,
                normal: 200,
                fast: 150,
                fastest: 40,
            };
            return delays[this.speed] || 200;
        },
    },
    methods: {
        open(event) {
            openPopoverInstance = this;
            this.hasBeenOpened = true;

            this.$nextTick(() => {
                this.visible = true;
                this.updatePosition(event);

                this.$nextTick(() => {
                    this.isMorphing = true;

                    setTimeout(() => {
                        this.isContentVisible = true;
                    }, this.contentDelay);
                });
            });

            if (this.closeOnClickOutside) {
                document.addEventListener('mousedown', this.handleClickOutside);
            }
            if (this.closeOnEsc) {
                document.addEventListener('keydown', this.handleEsc);
            }

            const scrollableParent = document.querySelector('.Wrap > span');
            if (scrollableParent) {
                scrollableParent.addEventListener('scroll', this.handleScroll);
            }

            this.$emit('open');
        },
        handleAnimationComplete(type) {
            if (type === 'open') {
                this.isMorphing = false;
            }
        },
        close() {
            this.visible = false;
            this.isContentVisible = false;
            if (openPopoverInstance === this) openPopoverInstance = null;

            if (this.closeOnClickOutside) {
                document.removeEventListener('mousedown', this.handleClickOutside);
            }
            if (this.closeOnEsc) {
                document.removeEventListener('keydown', this.handleEsc);
            }

            const scrollableParent = document.querySelector('.Wrap > span');
            if (scrollableParent) {
                scrollableParent.removeEventListener('scroll', this.handleScroll);
            }

            this.$emit('close');
        },
        handleClick(event) {
            if (this.disabled) return;

            if (this.visible) {
                this.close();
            } else {
                this.open(event);
            }
        },
        handleClickOutside(e) {
            if (
                !this.$refs.popover ||
                this.$refs.popover.contains(e.target) ||
                this.$refs.trigger.contains(e.target)
            )
                return;
            this.close();
        },
        handleEsc(e) {
            if (e.key === 'Escape') this.close();
        },
        handleScroll() {
            this.close();
        },
        updatePosition() {
            const triggerNode = this.$refs.triggerNode;
            const mainTrigger = this.$refs.trigger;
            const popover = this.$refs.popover;

            const triggerElement = triggerNode || mainTrigger;

            if (!triggerElement) return;
            let top, left;

            const rect = triggerElement.getBoundingClientRect();

            let parsedWidth = 300;
            if (this.width && this.width.includes('px')) {
                parsedWidth = parseInt(this.width);
            } else if (this.minWidth && this.minWidth.includes('px')) {
                parsedWidth = parseInt(this.minWidth);
            }

            const estimatedPopoverHeight = 200;
            const estimatedPopoverWidth = parsedWidth;
            const spacing = 6;

            const viewportHeight = window.innerHeight;
            const viewportWidth = window.innerWidth;

            const spaceBelow = viewportHeight - rect.bottom;
            const spaceAbove = rect.top;

            const shouldShowAbove =
                spaceBelow < estimatedPopoverHeight + spacing &&
                spaceAbove > estimatedPopoverHeight + spacing;

            if (shouldShowAbove) {
                top = rect.top - estimatedPopoverHeight - spacing;
                this.$refs.popover?.classList.add('popover-above');
                this.$refs.popover?.classList.remove('popover-below');
            } else {
                top = rect.bottom + spacing;
                this.$refs.popover?.classList.add('popover-below');
                this.$refs.popover?.classList.remove('popover-above');
            }

            let hPos = this.horizontalPosition;

            if (hPos === 'auto') {
                const spaceRight = viewportWidth - rect.left;
                const spaceLeft = rect.right;

                if (
                    spaceRight < estimatedPopoverWidth + spacing &&
                    spaceLeft > estimatedPopoverWidth + spacing
                ) {
                    hPos = 'right';
                } else if (
                    spaceRight >= estimatedPopoverWidth + spacing &&
                    spaceLeft >= estimatedPopoverWidth + spacing
                ) {
                    hPos = 'center';
                } else {
                    hPos = 'left';
                }
            }

            switch (hPos) {
                case 'right':
                    left = rect.right - estimatedPopoverWidth;
                    this.$refs.popover?.classList.add('popover-right');
                    this.$refs.popover?.classList.remove('popover-left', 'popover-center');
                    break;
                case 'center':
                    left = rect.left + rect.width / 2 - estimatedPopoverWidth / 2;
                    this.$refs.popover?.classList.add('popover-center');
                    this.$refs.popover?.classList.remove('popover-left', 'popover-right');
                    break;
                default:
                    left = rect.left;
                    this.$refs.popover?.classList.add('popover-left');
                    this.$refs.popover?.classList.remove('popover-right', 'popover-center');
                    break;
            }

            if (left < spacing) {
                left = spacing;
            } else if (left + estimatedPopoverWidth > viewportWidth - spacing) {
                left = viewportWidth - estimatedPopoverWidth - spacing;
            }

            const styleObj = {
                position: 'fixed',
                top: `${top}px`,
                left: `${left}px`,
                zIndex: 10000,
            };

            if (this.width) {
                styleObj.width = this.width;
            } else {
                styleObj.minWidth = this.minWidth;
                styleObj.maxWidth = this.maxWidth;
            }

            this.popoverStyle = styleObj;
        },
    },
    beforeDestroy() {
        if (openPopoverInstance === this) openPopoverInstance = null;
        if (this.closeOnClickOutside) {
            document.removeEventListener('mousedown', this.handleClickOutside);
        }
        if (this.closeOnEsc) {
            document.removeEventListener('keydown', this.handleEsc);
        }

        const scrollableParent = document.querySelector('.Wrap > span');
        if (scrollableParent) {
            scrollableParent.removeEventListener('scroll', this.handleScroll);
        }
    },
};
</script>

<style lang="scss" scoped>
.popover-trigger {
    cursor: pointer;

    &.is-disabled {
        cursor: not-allowed;
        opacity: 0.5;
    }
}

.popover-default-trigger {
    text-decoration: underline;
    font-weight: 500;
    color: var(--color-blue-dark);
    &:hover {
        color: var(--color-blue);
    }
}

.popover {
    background: var(--color-white);
    border-radius: 8px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
    border: 0.5px solid rgba(0, 0, 0, 0.08);
    padding: 12px;
    font-size: 0.85rem;
    position: fixed;
    z-index: 10000;

    &.is-morphing {
        background: transparent !important;
        border-color: transparent !important;
        box-shadow: none !important;
        pointer-events: none;
    }
}

.popover-content {
    display: flex;
    flex-direction: column;

    &.is-content-hidden {
        visibility: hidden;
        opacity: 0;
        pointer-events: none;
    }
}

.popover-default-content {
    margin-bottom: 8px;
}

.popover-actions {
    margin-top: 8px;
    padding-top: 8px;
    border-top: 0.5px solid rgba(0, 0, 0, 0.08);
    display: flex;
    gap: 8px;
    justify-content: flex-end;
}
</style>
